import React, { useContext } from "react";
import { Field } from "react-final-form";
import { IntlContext } from "context";
import { Select, SelectContent, SelectItem, SelectTrigger } from "components/ui/select";
import FieldGroup from "ui/Form/Element/FieldGroup";
import FieldControl from "ui/Form/Element/FieldControl";
import Input from "ui/Form/Element/Input";
import Label from "ui/Form/Element/Label";
import Feedback from "ui/Form/Element/Feedback";
import { cn } from "lib/utils";
import { selectContentClassName, selectItemClassName, selectTriggerClassName } from "./selectStyles";
import {
    buildE164Phone,
    getPhoneValidationError,
    splitE164Phone,
    stripToDigits,
    type PhoneDialOption,
} from "helpers/phone/phoneNumber";

export type PhonePrefixOption = PhoneDialOption & {
    countryIso2: string;
    label: string;
};

export const DEFAULT_PHONE_PREFIX_OPTIONS: PhonePrefixOption[] = [
    { countryIso2: "FR", dial: "+33", label: "France" },
    { countryIso2: "BE", dial: "+32", label: "Belgium" },
    { countryIso2: "CH", dial: "+41", label: "Switzerland" },
    { countryIso2: "LU", dial: "+352", label: "Luxembourg" },
    { countryIso2: "ES", dial: "+34", label: "Spain" },
    { countryIso2: "IT", dial: "+39", label: "Italy" },
    { countryIso2: "DE", dial: "+49", label: "Germany" },
];

const PHONE_FLAG_ICON_CLASS = "h-3 w-[18px] rounded-sm";

type PhoneFlagRect = { x?: number; y?: number; w?: number; h?: number; fill: string };

const PHONE_FLAG_RECTS: Record<string, PhoneFlagRect[]> = {
    FR: [{ x: 0, fill: "#0055A4" }, { x: 6, fill: "#FFFFFF" }, { x: 12, fill: "#EF4135" }],
    BE: [{ x: 0, fill: "#000000" }, { x: 6, fill: "#FFD90C" }, { x: 12, fill: "#EF3340" }],
    IT: [{ x: 0, fill: "#009246" }, { x: 6, fill: "#FFFFFF" }, { x: 12, fill: "#CE2B37" }],
    LU: [{ y: 0, h: 4, fill: "#ED2939" }, { y: 4, h: 4, fill: "#FFFFFF" }, { y: 8, h: 4, fill: "#00A1DE" }],
    DE: [{ y: 0, h: 4, fill: "#000000" }, { y: 4, h: 4, fill: "#DD0000" }, { y: 8, h: 4, fill: "#FFCE00" }],
    ES: [{ y: 0, h: 3, fill: "#AA151B" }, { y: 3, h: 6, fill: "#F1BF00" }, { y: 9, h: 3, fill: "#AA151B" }],
    CH: [{ w: 18, h: 12, x: 0, y: 0, fill: "#D52B1E" }, { w: 2.6, h: 7.2, x: 7.7, y: 2.4, fill: "#FFFFFF" }, { w: 7.2, h: 2.6, x: 5.4, y: 4.7, fill: "#FFFFFF" }],
};

const resolvePhoneFlagRect = (stripe: PhoneFlagRect) => {
    if (stripe.x !== undefined && stripe.w === undefined && stripe.h === undefined) {
        return { w: 6, h: 12, x: stripe.x, y: 0, fill: stripe.fill };
    }

    return {
        w: stripe.w ?? 18,
        h: stripe.h ?? 12,
        x: stripe.x ?? 0,
        y: stripe.y ?? 0,
        fill: stripe.fill,
    };
};

const FlagIcon: React.FC<{ iso2: string; className?: string }> = ({ iso2, className }) => {
    const code = String(iso2 ?? "").trim().toUpperCase();
    const flagRects = PHONE_FLAG_RECTS[code];

    if (flagRects) {
        return (
            <svg viewBox="0 0 18 12" className={cn(PHONE_FLAG_ICON_CLASS, className)} aria-hidden="true">
                {flagRects.map((stripe, index) => {
                    const rect = resolvePhoneFlagRect(stripe);
                    return (
                        <rect
                            key={index}
                            width={rect.w}
                            height={rect.h}
                            x={rect.x}
                            y={rect.y}
                            fill={rect.fill}
                        />
                    );
                })}
            </svg>
        );
    }

    // Fallback: show ISO2 in a small badge (more reliable than emoji fonts).
    return (
        <span
            className={cn(
                "inline-flex h-4 min-w-6 items-center justify-center rounded-sm bg-muted px-1 text-[10px] font-black text-foreground",
                className
            )}
            aria-hidden="true"
        >
            {code || "??"}
        </span>
    );
};

export type PhoneFieldProps = {
    name: string;
    label: React.ReactNode;
    required?: boolean;
    placeholder?: string;
    options?: PhonePrefixOption[];
    fallbackDial?: string;
    dialTriggerClassName?: string;
    fieldLabelClassName?: string;
    fieldInputClassName?: string;
};

const PhoneField: React.FC<PhoneFieldProps> = ({
    name,
    label,
    required,
    placeholder,
    options = DEFAULT_PHONE_PREFIX_OPTIONS,
    fallbackDial = "+33",
    dialTriggerClassName = "w-[110px]",
    fieldLabelClassName = "field__label",
    fieldInputClassName = "field-control__input",
}) => {
    const intl = useContext(IntlContext);

    return (
        <Field
            name={name}
            validate={(value) => getPhoneValidationError(value, intl.formatMessage)}
        >
            {({ input, meta }) => {
                const { dial, national } = splitE164Phone(input.value, options, fallbackDial);
                const currentOption =
                    options.find((o) => o.dial === dial) ??
                    options.find((o) => stripToDigits(o.dial) === stripToDigits(dial)) ??
                    options[0];

                const errorMessage = meta.error || meta.submitError;
                const error = Boolean(errorMessage);

                return (
                    <FieldGroup error={error}>
                        <Label className={fieldLabelClassName} error={error} required={Boolean(required)}>
                            {label}
                        </Label>

                        <FieldControl error={error}>
                            <div
                                className={cn(
                                    "flex h-12 w-full items-center overflow-hidden rounded-full border border-input bg-muted/50 text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0",
                                    error && "border-destructive focus-within:ring-destructive"
                                )}
                            >
                                <label className="sr-only" htmlFor={`${name}__dial`}>
                                    Phone country prefix
                                </label>
                                <Select
                                    value={currentOption?.dial ?? dial}
                                    onValueChange={(nextDial) => {
                                        const nextValue = buildE164Phone(nextDial, national, nextDial);
                                        input.onChange(nextValue);
                                    }}
                                    onOpenChange={(open) => {
                                        if (!open) {
                                            input.onBlur();
                                        }
                                    }}
                                >
                                    <SelectTrigger
                                        id={`${name}__dial`}
                                        aria-invalid={error}
                                        className={cn(
                                            selectTriggerClassName,
                                            dialTriggerClassName,
                                            "h-full shrink-0 rounded-none border-0 bg-transparent px-4 py-0 shadow-none focus:ring-0 focus:ring-offset-0",
                                        )}
                                    >
                                        {/* Use a div so SelectTrigger's `[&>span]:line-clamp-1` doesn't override flex layout */}
                                        <div className="flex flex-nowrap items-center gap-2 whitespace-nowrap leading-none">
                                            <FlagIcon iso2={currentOption?.countryIso2 ?? ""} />
                                            <span className="whitespace-nowrap">{currentOption?.dial ?? dial}</span>
                                        </div>
                                    </SelectTrigger>
                                    <SelectContent sideOffset={8} className={selectContentClassName}>
                                        {options.map((o) => (
                                            <SelectItem key={o.dial} value={o.dial} className={cn(selectItemClassName, "py-2 pl-8 pr-3")}>
                                                <span className="flex flex-nowrap items-center gap-2 whitespace-nowrap">
                                                    <FlagIcon iso2={o.countryIso2} />
                                                    <span>{o.dial}</span>
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>

                                <div className="h-7 w-px bg-input" aria-hidden="true" />

                                <Input
                                    {...input}
                                    name={input.name}
                                    required={Boolean(required)}
                                    type="tel"
                                    placeholder={placeholder ?? "ex: 6 12 34 56 78"}
                                    autoComplete="tel"
                                    className={cn(
                                        // allow caller styling first (font/size), then enforce "no inner pill"
                                        fieldInputClassName,
                                        "h-full flex-1 rounded-none border-0 bg-transparent px-5 py-0 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
                                    )}
                                    value={national}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        input.onChange(buildE164Phone(dial, e.target.value));
                                    }}
                                    onBlur={input.onBlur}
                                />
                            </div>
                        </FieldControl>

                        {error && errorMessage !== undefined && (
                            <Feedback className="field__feedback error">{errorMessage}</Feedback>
                        )}
                    </FieldGroup>
                );
            }}
        </Field>
    );
};

export default PhoneField;

